import React, { useEffect } from "react";
import type { FormInstance } from "antd";
import { Form, Input } from "../../../../shared";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import DisplayLocationsField from "../../controls/DisplayLocationsField";
import FreeformBannerEditor from "../../controls/FreeformBannerEditor";
import { displayLocationsRequiredRule } from "./displayLocationDefaults";
import { formFieldTooltip } from "./formFieldTooltip";
import {
  DEFAULT_FREEFORM_BANNER_LAYOUT,
  DISPLAY_BANNER_RESULT_TYPE_KEY,
  type FreeformBannerLayout,
  type WizardFormValues,
  type WizardStepFormValue,
} from "./toWizardCampaignIn";
import styles from "../../stylesDynamicBonuses.module.css";

type Props = {
  form: FormInstance<WizardFormValues>;
  rulesLocked?: boolean;
  /** View-only host (series child). Dims Display Locations like Priority. */
  readOnly?: boolean;
};

/**
 * Registers an object-valued form field without rendering a control. AntD only
 * persists nested values reliably when a Form.Item is mounted for the path.
 */
const FormObjectAnchor: React.FC<{
  value?: FreeformBannerLayout;
  onChange?: (value: FreeformBannerLayout) => void;
}> = () => null;

/**
 * Standalone marketing banner authoring (BONENG-813) — surfaces + freeform
 * creative. No bonus result / trigger steps.
 */
const DisplayBannerStep: React.FC<Props> = ({
  form,
  rulesLocked = false,
  readOnly = false,
}) => {
  useEffect(() => {
    const step = (form.getFieldValue(["steps", 0]) ?? {}) as WizardStepFormValue;
    if (step.result_type_key !== DISPLAY_BANNER_RESULT_TYPE_KEY) {
      form.setFieldValue(
        ["steps", 0, "result_type_key"],
        DISPLAY_BANNER_RESULT_TYPE_KEY,
      );
    }
    if (!step.freeform_layout) {
      form.setFieldValue(
        ["steps", 0, "freeform_layout"],
        DEFAULT_FREEFORM_BANNER_LAYOUT,
      );
    }
    if (step.banner?.claim?.visible === undefined) {
      form.setFieldValue(["steps", 0, "banner", "claim", "visible"], true);
    }
    if (!step.display_locations?.length) {
      form.setFieldValue(["steps", 0, "display_locations"], [
        "casino",
        "discover",
      ]);
    }
  }, [form]);

  return (
    <div
      className={rulesLocked ? styles.readOnlyFields : undefined}
      data-testid="display-banner-step"
    >
      <Form.Item name={["steps", 0, "result_type_key"]} hidden>
        <Input />
      </Form.Item>
      <Form.Item
        name={["steps", 0, "freeform_layout"]}
        hidden
        initialValue={DEFAULT_FREEFORM_BANNER_LAYOUT}
      >
        <FormObjectAnchor />
      </Form.Item>

      <div className={styles.displayBannerStepHeader}>
        <h3 className={styles.displayBannerStepTitle}>
          {AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_SECTION_TITLE}
        </h3>
        <p className={styles.displayBannerStepHint}>
          {AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_SECTION_HINT}
        </p>
      </div>

      <Form.Item
        label={AppStrings.DISPLAY_LOCATIONS}
        tooltip={formFieldTooltip(
          AppStrings.PAM_DYNAMIC_BONUS_DISPLAY_BANNER_SURFACES_HINT,
        )}
        name={["steps", 0, "display_locations"]}
        rules={[displayLocationsRequiredRule]}
      >
        <DisplayLocationsField readOnly={readOnly} />
      </Form.Item>

      <FreeformBannerEditor
        form={form}
        bannerPath={["steps", 0, "banner"]}
        bannerItemPath={["steps", 0, "banner"]}
        layoutPath={["steps", 0, "freeform_layout"]}
      />
    </div>
  );
};

export default DisplayBannerStep;
